import { Box, ScrollArea } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { AppTitle } from "@/components/utils"
import { useUsers } from "@/hooks"
import type { TUser } from "@/types"
import { buildUserURL } from "@/utils"
import { UserLine } from "../UserLine"
import { BlockSkeleton, Panel } from "./utils"

export function ProfileTeam({ user }: { user: TUser }) {
  const { t } = useTranslation()
  const { users, isPending } = useUsers()

  if (!users) return null

  const manager = users.find((u) => u.id === user.manager_id)
  const teams = new Set(user.team_ids || [])
  const subordinates = users.filter((u) => u.manager_id === user.id)
  const teammates = users.filter(
    (u) =>
      !subordinates.includes(u) &&
      u.id !== user.id &&
      u.id !== user.manager_id &&
      u.team_ids?.some((teamId) => teams.has(teamId)),
  )

  if (!manager && !subordinates.length && !teammates.length) return null

  return (
    <Panel
      block={!isPending}
      lg={{
        gridRowStart: 1,
        gridColumnStart: 3,
        gridRowEnd: "span 2",
      }}
      md={{
        gridColumnStart: 2,
        gridColumnEnd: "span 1",
      }}
      sm={{
        gridColumnEnd: "span 2",
      }}
    >
      {isPending ? (
        <BlockSkeleton />
      ) : (
        <ScrollArea h={800} type="hover">
          {manager && (
            <Box mb="lg">
              <AppTitle>{t("Supervisor")}</AppTitle>
              <UserLine user={manager} my="sm" url={buildUserURL(manager.id)} />
            </Box>
          )}
          {subordinates.length > 0 && (
            <Box mb="lg">
              <AppTitle>{t("Direct reports")}</AppTitle>
              {subordinates.map((user) => (
                <UserLine key={user.id} user={user} url={buildUserURL(user.id)} my="sm" />
              ))}
            </Box>
          )}
          {teammates.length > 0 && (
            <Box mb="lg">
              <AppTitle>{t("Teammates")}</AppTitle>
              {teammates.map((user) => (
                <UserLine key={user.id} user={user} url={buildUserURL(user.id)} my="sm" />
              ))}
            </Box>
          )}
        </ScrollArea>
      )}
    </Panel>
  )
}
